import type { MapColumnsFn } from '@utils/createCSVReportFile';
import { ExportCSVReport } from '@utils/createCSVReportFile';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { ChapterEnum, FileInput, UerClassifierEnum } from 'gql/graphql';

import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
    catalogImportUerBaseCostWorkTypesGql,
    catalogUpsertArchiveUerBaseCostBasisesGql,
    catalogUpsertMultiUerBaseCostBasisesGql,
} from '../../../catalogUerBaseCostQueries';
import { MassUploadType } from '../models/constants';
import type { SelectOption } from '../models/types';

const estimateBasisFilesRequestId = 'catalogParseMultiUerBaseCostBasises';
const estimateBasisZipRequestId = 'catalogParseArchiveUerBaseCostBasises';
const importWorkTypesRequestId = 'catalogImportUerBaseCostWorkTypes';

type MakeRequestParams = {
    files?: FileInput[];
    uerClassifier: SelectOption<UerClassifierEnum> | null;
    chapter: SelectOption<ChapterEnum> | null;
    justification: string;
};

interface IUseUerBaseCostMassUpload {
    uploadType: MassUploadType;
    catalogId: number;
    onSuccessAction: () => void;
}

const customHeaderWorkTypes = 'Номер; УЕР; Сообщение';
const customHeaderZipData = 'Номер; Файл; Сообщение';
const customHeaderEstimateBasisFiles = 'Номер; Сообщение';
const mapColumns: MapColumnsFn = (msg, idx) =>
    msg?.source ? `${idx}; ${msg?.source}; ${msg?.message}` : `${idx}; ${msg?.message}`;

export const useUerBaseCostMassUpload = ({ uploadType, catalogId, onSuccessAction }: IUseUerBaseCostMassUpload) => {
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const dispatch = useDispatch();

    const [estimateBasisFilesData, estimateBasisFilesStatus, estimateBasisFilesErrors, estimateBasisFilesRequest] =
        useRequest({
            requestId: estimateBasisFilesRequestId,
            query: catalogUpsertMultiUerBaseCostBasisesGql,
            withEffect: false,
            dependenciesUpdater: onSuccessAction,
        });

    const [estimateBasisZipData, estimateBasisZipStatus, estimateBasisZipErrors, estimateBasisZipRequest] = useRequest({
        requestId: estimateBasisZipRequestId,
        query: catalogUpsertArchiveUerBaseCostBasisesGql,
        withEffect: false,
        dependenciesUpdater: onSuccessAction,
    });

    const [importWorkTypesData, importWorkTypesStatus, importWorkTypesErrors, importWorkTypesRequest] = useRequest({
        requestId: importWorkTypesRequestId,
        query: catalogImportUerBaseCostWorkTypesGql,
        withEffect: false,
        dependenciesUpdater: onSuccessAction,
    });

    const [resetEstimateBasisFilesData] = useResetStoreData({
        requestId: estimateBasisFilesRequestId,
    });

    const [resetEstimateBasisZipData] = useResetStoreData({
        requestId: estimateBasisZipRequestId,
    });

    const [resetImportWorkTypesData] = useResetStoreData({
        requestId: importWorkTypesRequestId,
    });

    const uploadStateByType = useMemo(
        () => ({
            [MassUploadType.EstimateBasisFiles]: {
                data: estimateBasisFilesData?.catalogUpsertMultiUerBaseCostBasises,
                dataResult: estimateBasisFilesData?.catalogUpsertMultiUerBaseCostBasises?.result,
                dataHasValidation: estimateBasisFilesData?.catalogUpsertMultiUerBaseCostBasises?.hasValidation,
                validation: estimateBasisFilesData?.catalogUpsertMultiUerBaseCostBasises?.validation,
                status: estimateBasisFilesStatus,
                errors: estimateBasisFilesErrors,
                reset: resetEstimateBasisFilesData,
            },
            [MassUploadType.EstimateBasisZip]: {
                data: estimateBasisZipData?.catalogUpsertArchiveUerBaseCostBasises,
                dataResult: estimateBasisZipData?.catalogUpsertArchiveUerBaseCostBasises?.result,
                dataHasValidation: estimateBasisZipData?.catalogUpsertArchiveUerBaseCostBasises?.hasValidation,
                validation: estimateBasisZipData?.catalogUpsertArchiveUerBaseCostBasises?.validation,
                status: estimateBasisZipStatus,
                errors: estimateBasisZipErrors,
                reset: resetEstimateBasisZipData,
            },
            [MassUploadType.WorkScopes]: {
                data: importWorkTypesData?.catalogImportUerBaseCostWorkTypes,
                dataResult: importWorkTypesData?.catalogImportUerBaseCostWorkTypes?.result,
                dataHasValidation: importWorkTypesData?.catalogImportUerBaseCostWorkTypes?.hasValidation,
                validation: importWorkTypesData?.catalogImportUerBaseCostWorkTypes?.validation,
                status: importWorkTypesStatus,
                errors: importWorkTypesErrors,
                reset: resetImportWorkTypesData,
            },
        }),
        [
            estimateBasisFilesData,
            estimateBasisFilesErrors,
            estimateBasisFilesStatus,
            estimateBasisZipData,
            estimateBasisZipErrors,
            estimateBasisZipStatus,
            importWorkTypesData,
            importWorkTypesErrors,
            importWorkTypesStatus,
            resetEstimateBasisFilesData,
            resetEstimateBasisZipData,
            resetImportWorkTypesData,
        ],
    );

    const currentUploadState = uploadStateByType[uploadType as keyof typeof uploadStateByType];

    const makeRequest = ({ files, uerClassifier, chapter, justification }: MakeRequestParams) => {
        if (!files?.length) {
            return;
        }

        if (uploadType === MassUploadType.EstimateBasisFiles) {
            setIsSubmitted(true);
            dispatch(
                estimateBasisFilesRequest({
                    input: {
                        contents: files,
                        uerClassifier: uerClassifier?.id,
                        chapter: chapter?.id,
                        justification,
                        catalogId,
                    },
                }),
            );

            return;
        }

        if (uploadType === MassUploadType.EstimateBasisZip) {
            setIsSubmitted(true);
            dispatch(
                estimateBasisZipRequest({
                    input: {
                        content: files[0],
                        uerClassifier: uerClassifier?.id,
                        chapter: chapter?.id,
                        justification,
                        catalogId,
                    },
                }),
            );

            return;
        }

        if (uploadType === MassUploadType.WorkScopes) {
            setIsSubmitted(true);
            dispatch(
                importWorkTypesRequest({
                    file: files[0],
                    catalogId,
                }),
            );
        }
    };

    const { status, errors, data, dataResult, dataHasValidation, validation } = currentUploadState;

    const resetUploadData = () => {
        currentUploadState?.reset(undefined);
    };

    const handleCreateReport = () => {
        const customHeader = {
            [MassUploadType.WorkScopes]: customHeaderWorkTypes,
            [MassUploadType.EstimateBasisZip]: customHeaderZipData,
            [MassUploadType.EstimateBasisFiles]: customHeaderEstimateBasisFiles,
        }[uploadType];

        ExportCSVReport(validation, customHeader, mapColumns);
    };

    return {
        makeRequest,
        resetUploadData,
        status,
        errors,
        data,
        dataResult,
        dataHasValidation,
        isSubmitted,
        handleCreateReport,
    };
};
